import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Globe,
  Check,
  X,
  Shield,
  Zap,
  RefreshCw,
  ExternalLink,
  Loader2,
  Settings,
} from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, lowestPrice } from '@/lib/seo';

const popularTLDs = [
  { tld: '.de', price: '0.99', popular: true },
  { tld: '.com', price: '9.99', popular: true },
  { tld: '.net', price: '12.99' },
  { tld: '.org', price: '12.99' },
  { tld: '.eu', price: '4.99' },
  { tld: '.info', price: '2.99' },
  { tld: '.io', price: '39.99' },
  { tld: '.dev', price: '14.99' },
  { tld: '.app', price: '14.99' },
  { tld: '.shop', price: '2.99' },
  { tld: '.online', price: '1.99' },
  { tld: '.store', price: '2.99' },
];

export default function DomainsPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(null);

  const features = [
    { icon: Shield,   titleKey: 'domains.why1_title', descKey: 'domains.why1_desc' },
    { icon: Zap,      titleKey: 'domains.why2_title', descKey: 'domains.why2_desc' },
    { icon: Settings, titleKey: 'domains.why3_title', descKey: 'domains.why3_desc' },
    { icon: Globe,    titleKey: 'domains.why4_title', descKey: 'domains.why4_desc' },
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error(t('domains.search_error'));
      return;
    }

    setIsSearching(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const domain = searchQuery.toLowerCase().replace(/\s+/g, '');
    const baseDomain = domain.includes('.') ? domain.split('.')[0] : domain;
    
    const mockResults = popularTLDs.slice(0, 6).map(tld => ({
      domain: `${baseDomain}${tld.tld}`,
      available: Math.random() > 0.3,
      price: tld.price,
      tld: tld.tld,
    }));
    
    setResults(mockResults);
    setIsSearching(false);
  };

  return (
    <Layout>
      <SEOHead
        title="Register Domains – .de, .com, .eu & more"
        description={`Register your domain from €${lowestPrice(popularTLDs.map((d) => d.price))}/year: .de, .com, .net, .eu, .io, .shop and more. Fast registration at a fair price with DeinServerHost.`}
        canonical="/domains"
        schema={productSchema({
          name: 'Domain Registration',
          description: 'Domain registration for .de, .com, .net, .org, .eu, .io and more TLDs.',
          path: '/domains',
          offers: popularTLDs.map((d) => ({ name: `${d.tld} domain`, price: d.price, period: 'year' })),
        })}
      />
      <div className="section-spacing">
        <div className="container-main">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('domains.title')} <span className="text-gradient">{t('domains.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('domains.subtitle')}
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto mb-16"
          >
            <form onSubmit={handleSearch} className="glass rounded-2xl p-6">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={t('domains.search_placeholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 text-lg"
                    data-testid="domain-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSearching}
                  className="bg-primary hover:bg-primary/90 h-14 px-8 font-bold uppercase tracking-wider"
                  data-testid="domain-search-btn"
                >
                  {isSearching ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      {t('domains.search_btn')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Search Results */}
          {results && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-xl font-bold mb-6 text-center">{t('domains.search_results')}</h2>
              <div className="space-y-3">
                {results.map((result, index) => (
                  <motion.div
                    key={result.domain}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      result.available 
                        ? 'border-green-500/30 bg-green-500/5' 
                        : 'border-border bg-card/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {result.available ? (
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-500" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center">
                          <X className="w-5 h-5 text-destructive" />
                        </div>
                      )}
                      <div>
                        <span className="font-bold">{result.domain}</span>
                        <span className={`ml-2 text-sm ${result.available ? 'text-green-500' : 'text-muted-foreground'}`}>
                          {result.available ? t('domains.available') : t('domains.taken')}
                        </span>
                      </div>
                    </div>
                    {result.available && (
                      <div className="flex items-center gap-4">
                        <span className="text-xl font-bold text-primary">€{result.price}</span>
                        <span className="text-sm text-muted-foreground">{t('domains.perYear')}</span>
                        <a
                          href={`https://deinserverhost.de/store/cart.php?a=add&domain=register&query=${result.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" className="bg-primary hover:bg-primary/90">
                            {t('domains.register')}
                          </Button>
                        </a>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Popular TLDs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-center mb-8">{t('domains.tldTitle')}</h2>
            <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">{t('domains.tldSubtitle')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {popularTLDs.map((tld, index) => (
                <motion.div
                  key={tld.tld}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative"
                >
                  <Card className="text-center hover:border-primary/50 transition-colors cursor-pointer">
                    {tld.popular && (
                      <Badge className="absolute -top-2 -right-2 bg-primary">{t('domains.popular_tlds')}</Badge>
                    )}
                    <CardContent className="pt-6">
                      <div className="text-2xl font-black mb-2">{tld.tld}</div>
                      <div className="text-sm text-muted-foreground">{t('common.from')}</div>
                      <div className="text-xl font-bold text-primary">€{tld.price}</div>
                      <div className="text-xs text-muted-foreground">{t('domains.perYear')}</div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Why section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-center mb-8">{t('domains.whyTitle')}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => (
                <Card key={feature.titleKey} className="text-center">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-bold mb-2">{t(feature.titleKey)}</h3>
                    <p className="text-sm text-muted-foreground">{t(feature.descKey)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          {/* Transfer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 text-center p-8 glass rounded-2xl"
          >
            <RefreshCw className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">{t('domains.ctaTitle')}</h2>
            <a
              href="https://deinserverhost.de/store/cart.php?a=add&domain=transfer"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                {t('domains.ctaBtn')}
                <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
